// src/pages/Admin/DossierInscription/EditDossierInscriptionModal.jsx
import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import {
  FaSave,
  FaTimes,
  FaUpload,
  FaExternalLinkAlt,
  FaTrashAlt,
  FaGoogleDrive,
  FaFileAlt,
  FaCheck,
  FaTimesCircle,
} from "react-icons/fa";
import Button from "./../../../components/Button/Button";
import {
  updateDossierInscription,
  uploadCertificatMedical,
  uploadFormulaireLicenceSigne,
  uploadLicenceDefinitive,
  deleteDossierFile,
} from "./../../../services/dossierServices";

/* Helpers */
const apiBase = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const toPublicUrl = (u) =>
  u && u.startsWith("/uploads/") ? `${apiBase}${u}` : u;
const isLocalFileUrl = (u) => !!u && u.startsWith("/uploads/");
const isDriveUrl = (u) =>
  !!u &&
  /(drive\.google\.com|docs\.google\.com|googleusercontent\.com)/i.test(u);
const todayStr = () => new Date().toISOString().slice(0, 10);

// Clé canonique en base + alias de compat
const SIGNEE_FIELD = "formulaire_licence_signe";

/* UI sous-composants */
function BigCheck({ name, checked, onChange, label }) {
  return (
    <label className="flex items-center gap-3 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 px-4 py-3 bg-white/90 dark:bg-slate-900/40">
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        className="h-5 w-5 rounded border-slate-300 text-violet-600 focus:ring-violet-400"
      />
      <span className="text-sm font-medium">{label}</span>
    </label>
  );
}

function ModeToggle({ value, onChange }) {
  return (
    <div className="inline-flex rounded-lg overflow-hidden ring-1 ring-slate-300 dark:ring-slate-700">
      <button
        type="button"
        onClick={() => onChange("upload")}
        className={`px-3 py-1.5 text-xs font-medium ${
          value === "upload"
            ? "bg-slate-900 text-white"
            : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
        }`}
      >
        Fichier
      </button>
      <button
        type="button"
        onClick={() => onChange("link")}
        className={`px-3 py-1.5 text-xs font-medium ${
          value === "link"
            ? "bg-slate-900 text-white"
            : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
        }`}
      >
        Lien
      </button>
    </div>
  );
}

function Section({ title, children, rightAddon }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4">
      <div className="flex items-start justify-between mb-3">
        <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          {title}
        </h4>
        {rightAddon}
      </div>
      {children}
    </div>
  );
}

/** Ligne "état actuel" + actions Ouvrir / Supprimer / Remplacer  */
function CurrentDoc({
  label,
  url,
  onOpen,
  onDelete,
  onReplaceClick,
  replacing,
}) {
  const has = !!url;
  const Icon = has
    ? isDriveUrl(url)
      ? FaGoogleDrive
      : FaFileAlt
    : FaTimesCircle;
  const iconClass = has
    ? isDriveUrl(url)
      ? "text-[#1a73e8]"
      : "text-violet-500"
    : "text-slate-400";

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/50 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-slate-800 dark:text-slate-100">
          <Icon className={iconClass} />
          <div className="text-sm">
            <div className="font-medium">{label}</div>
            <div className="text-xs text-slate-600 dark:text-slate-300">
              {has
                ? isLocalFileUrl(url)
                  ? "(Fichier serveur)"
                  : "(Lien)"
                : "Aucun document"}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            type="button"
            onClick={onOpen}
            text="Ouvrir"
            icon={FaExternalLinkAlt}
            className="!rounded-xl gap-2"
            disabled={!has}
          />
          {has && (
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ring-1 ring-rose-600/30 text-rose-600 hover:bg-white"
              title="Supprimer"
            >
              <FaTrashAlt />
              Supprimer
            </button>
          )}
        </div>
      </div>

      {has && !replacing && (
        <div className="mt-3">
          <button
            type="button"
            onClick={onReplaceClick}
            className="text-xs underline text-slate-700 dark:text-slate-300 hover:opacity-80"
          >
            Remplacer par un nouveau document
          </button>
        </div>
      )}
    </div>
  );
}

/* Composant principal */
export default function EditDossierInscriptionModal({
  isOpen,
  onClose,
  dossier,
  refreshList,
}) {
  const dialogRef = useRef(null);

  // ------- état global -------
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({
    cm: false,
    signee: false,
    lic: false,
  });

  // champs & validations
  const [dateCm, setDateCm] = useState("");
  const [validateCM, setValidateCM] = useState(false);
  const [validateLic, setValidateLic] = useState(false);
  const [validateRegl, setValidateRegl] = useState(false);

  // urls actuelles
  const [cmUrl, setCmUrl] = useState("");
  const [formSignerUrl, setFormSignerUrl] = useState("");
  const [formSigneeUrl, setFormSigneeUrl] = useState("");
  const [licenceUrl, setLicenceUrl] = useState("");
  const [driveUrl, setDriveUrl] = useState("");

  // modes + fichiers
  const [cmMode, setCmMode] = useState("upload");
  const [cmFile, setCmFile] = useState(null);
  const [cmReplacing, setCmReplacing] = useState(false);

  const [signeeMode, setSigneeMode] = useState("upload");
  const [signeeFile, setSigneeFile] = useState(null);
  const [signeeReplacing, setSigneeReplacing] = useState(false);

  const [licMode, setLicMode] = useState("upload");
  const [licFile, setLicFile] = useState(null);
  const [licReplacing, setLicReplacing] = useState(false);

  // hydrate à l’ouverture
  useEffect(() => {
    if (!isOpen || !dossier) return;

    setCmUrl(dossier.cm_url || "");
    setDateCm(
      dossier.date_cm
        ? new Date(dossier.date_cm).toISOString().slice(0, 10)
        : ""
    );
    setValidateCM(!!dossier.validate_cm);

    setFormSignerUrl(dossier.formulaire_licence_a_signer || "");
    const signeeFrom = dossier.formulaire_licence_signe || "";
    setFormSigneeUrl(signeeFrom);
    setLicenceUrl(dossier.licence_url || "");

    setValidateLic(!!dossier.validate_licence);
    setValidateRegl(!!dossier.validate_reglement);
    setDriveUrl(dossier.lien_drive || "");

    setCmMode(
      dossier.cm_url && isLocalFileUrl(dossier.cm_url) ? "upload" : "link"
    );
    setSigneeMode(signeeFrom && isLocalFileUrl(signeeFrom) ? "upload" : "link");
    setLicMode(
      dossier.licence_url && isLocalFileUrl(dossier.licence_url)
        ? "upload"
        : "link"
    );

    setCmFile(null);
    setSigneeFile(null);
    setLicFile(null);

    setCmReplacing(false);
    setSigneeReplacing(false);
    setLicReplacing(false);
  }, [isOpen, dossier]); // eslint-disable-line

  // lock scroll + ESC + focus
  useEffect(() => {
    if (!isOpen) return;
    const root = document.documentElement;
    const prev = root.style.overflow;
    root.style.overflow = "hidden";

    const t = setTimeout(() => {
      dialogRef.current?.focus();
    }, 0);

    const onEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onEsc);

    return () => {
      root.style.overflow = prev;
      window.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  if (!dossier?.id) return null;

  // L'état initial côté backend (ne bouge pas pendant la saisie)
  const hadCmInitial = !!dossier?.cm_url;
  const hadSigneeInitial = !!dossier?.formulaire_licence_signe;
  const hadLicInitial = !!dossier?.licence_url;

  const adherentName = dossier?.Adherent
    ? `${dossier.Adherent.nom || ""} ${dossier.Adherent.prenom || ""}`.trim()
    : "";

  // ------- actions génériques -------
  const openUrl = (u) =>
    u && window.open(toPublicUrl(u), "_blank", "noopener,noreferrer");

  const removeField = async (field, label) => {
    if (!window.confirm(`Supprimer « ${label} » ?`)) return;
    try {
      setSaving(true);

      // si on reçoit l’alias, on mappe vers la clé canonique pour l’API
      const apiField = field === SIGNEE_FIELD ? SIGNEE_FIELD : field;
      await deleteDossierFile(dossier.id, apiField);

      if (field === "cm_url") setCmUrl("");
      if (field === "formulaire_licence_signe" || field === SIGNEE_FIELD)
        setFormSigneeUrl("");
      if (field === "licence_url") setLicenceUrl("");
      if (field === "formulaire_licence_a_signer") setFormSignerUrl("");
      if (field === "lien_drive") setDriveUrl("");

      toast.success("Document supprimé.");
      await refreshList?.();
    } catch (e) {
      console.error(e);
      toast.error("Échec de la suppression.");
    } finally {
      setSaving(false);
    }
  };

  // ------- handlers CM -------
  const submitCM = async () => {
    if (!dateCm) return toast.error("Indiquez la date exacte du certificat.");
    try {
      setUploading((u) => ({ ...u, cm: true }));
      if (cmMode === "upload") {
        if (!cmFile)
          return toast.error("Choisissez un fichier (PDF/JPG/PNG/WEBP).");
        const data = await uploadCertificatMedical(dossier.id, cmFile, {
          date_cm: dateCm,
        });
        setCmUrl(data?.cm_url || "");
        setDateCm(
          data?.date_cm
            ? new Date(data.date_cm).toISOString().slice(0, 10)
            : dateCm
        );
        toast.success("Certificat téléversé.");
      } else {
        if (isLocalFileUrl(cmUrl)) {
          try {
            await deleteDossierFile(dossier.id, "cm_url");
          } catch {}
        }
        await updateDossierInscription(dossier.id, {
          cm_url: (cmUrl || "").trim(),
          date_cm: dateCm,
        });
        toast.success("Lien et date enregistrés.");
      }
      setCmReplacing(false);
      await refreshList?.();
    } catch (e) {
      console.error(e);
      toast.error("Échec sur le certificat.");
    } finally {
      setUploading((u) => ({ ...u, cm: false }));
    }
  };

  // ------- handlers Formulaire signé -------
  const submitSignee = async () => {
    try {
      setUploading((u) => ({ ...u, signee: true }));

      if (signeeMode === "upload") {
        // ... upload du fichier
      } else {
        // MODE LIEN
        if (formSigneeUrl && !/^https?:\/\//i.test(formSigneeUrl)) {
          toast.error("URL du formulaire signé invalide.");
          return;
        }
        const value = (formSigneeUrl || "").trim();

        await updateDossierInscription(dossier.id, {
          formulaire_licence_signe: value,
        });

        setFormSigneeUrl(value);
        toast.success("Lien du formulaire signé enregistré.");
      }

      setSigneeReplacing(false);
      await refreshList?.();
    } catch (e) {
      console.error(e);
      toast.error("Échec sur le formulaire signé.");
    } finally {
      setUploading((u) => ({ ...u, signee: false }));
    }
  };

  // ------- handlers Licence -------
  const submitLicence = async () => {
    try {
      setUploading((u) => ({ ...u, lic: true }));
      if (licMode === "upload") {
        if (!licFile)
          return toast.error("Choisissez un fichier (PDF/JPG/PNG/WEBP).");
        const data = await uploadLicenceDefinitive(dossier.id, licFile);
        setLicenceUrl(data?.licence_url || "");
        toast.success("Licence définitive téléversée.");
      } else {
        if (licenceUrl && !/^https?:\/\//i.test(licenceUrl)) {
          toast.error("URL de la licence invalide.");
          return;
        }
        if (isLocalFileUrl(dossier.licence_url)) {
          try {
            await deleteDossierFile(dossier.id, "licence_url");
          } catch {}
        }
        await updateDossierInscription(dossier.id, {
          licence_url: (licenceUrl || "").trim(),
        });
        toast.success("Lien de la licence enregistré.");
      }
      setLicReplacing(false);
      await refreshList?.();
    } catch (e) {
      console.error(e);
      toast.error("Échec sur la licence.");
    } finally {
      setUploading((u) => ({ ...u, lic: false }));
    }
  };

  // ------- submit global (checkbox + drive + signer) -------
  const submitMeta = async (e) => {
    e?.preventDefault?.();
    try {
      setSaving(true);

      const valueSignee = (formSigneeUrl || "").trim() || null;

      const payload = {
        // validations
        validate_cm: !!validateCM,
        validate_licence: !!validateLic,
        validate_reglement: !!validateRegl,

        // liens
        formulaire_licence_a_signer: (formSignerUrl || "").trim() || null,
        formulaire_licence_signe: (formSigneeUrl || "").trim() || null,
        lien_drive: (driveUrl || "").trim() || null,

        // ⚠️ toujours synchroniser la date CM depuis la modale
        // - si validé et pas de date => date du jour
        // - si non validé => on efface la date (cohérent avec le toggle)
        date_cm: validateCM ? dateCm || todayStr() : null,
      };

      // sécurité : si CM validé sans date, on pose la date du jour
      if (payload.validate_cm && !dateCm) {
        payload.date_cm = todayStr();
      }

      await updateDossierInscription(dossier.id, payload);
      console.log("[DS] updateDossierInscription ←", {
        dossierId: dossier.id,
        payload,
      });

      toast.success("Dossier mis à jour.");
      await refreshList?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’enregistrement.");
      console.log("[DS] updateDossierInscription ERROR", {
        dossierId: dossier.id,
        error: e,
      });
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      role="dialog"
      aria-modal="true"
      aria-label="Éditer un dossier d’inscription"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="relative w-full max-w-5xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh]"
        style={{ WebkitOverflowScrolling: "touch" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
          <div>
            <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
              Dossier d’inscription — {adherentName || "—"}
            </h3>
            <p className="text-white/90 text-xs sm:text-sm">
              Gérez certificat médical, licence & liens — avec ouverture,
              suppression, remplacement.
            </p>
          </div>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
            aria-label="Fermer"
            type="button"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Corps scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-6">
          {/* 1) CERTIFICAT MÉDICAL */}
          <Section
            title="1) Santé — Certificat / Attestation"
            rightAddon={
              <div className="flex items-center gap-3">
                <div className="text-xs text-slate-600 dark:text-slate-300">
                  <label className="mr-2">Date du certificat</label>
                  <input
                    type="date"
                    value={dateCm || ""}
                    onChange={(e) => setDateCm(e.target.value)}
                    className="rounded-lg border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs bg-white dark:bg-slate-900"
                  />
                </div>
                <BigCheck
                  name="validate_cm"
                  label="Validé"
                  checked={validateCM}
                  onChange={(e) => setValidateCM(e.target.checked)}
                />
              </div>
            }
          >
            <CurrentDoc
              label="Document actuel"
              url={cmUrl}
              onOpen={() => openUrl(cmUrl)}
              onDelete={() => removeField("cm_url", "Certificat médical")}
              onReplaceClick={() => setCmReplacing(true)}
              replacing={cmReplacing}
            />

            {(!hadCmInitial || cmReplacing) && (
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <ModeToggle value={cmMode} onChange={setCmMode} />
                </div>

                {cmMode === "upload" ? (
                  <div className="grid sm:grid-cols-[1fr,auto] gap-3 items-end">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Fichier (PDF/JPG/PNG/WEBP)
                      </label>
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => setCmFile(e.target.files?.[0] || null)}
                        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                      />
                      {isLocalFileUrl(cmUrl) && (
                        <p className="mt-2 text-xs text-slate-500">
                          Un fichier local est déjà présent. Le nouveau le
                          remplacera.
                        </p>
                      )}
                    </div>
                    <Button
                      type="button"
                      onClick={submitCM}
                      icon={FaUpload}
                      text={uploading.cm ? "Téléversement…" : "Enregistrer"}
                      disabled={
                        uploading.cm ||
                        (cmMode === "upload" && !cmFile) ||
                        !dateCm
                      }
                      className="!rounded-xl gap-2"
                    />
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-[1fr,auto] gap-3 items-end">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Lien (Drive/Autre){" "}
                        {cmUrl ? (
                          <FaCheck className="inline ml-2 text-green-500" />
                        ) : null}
                      </label>
                      <input
                        type="url"
                        value={cmUrl}
                        onChange={(e) => setCmUrl(e.target.value)}
                        placeholder="https://…"
                        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={submitCM}
                      icon={FaSave}
                      text={uploading.cm ? "Enregistrement…" : "Enregistrer"}
                      disabled={
                        uploading.cm ||
                        !cmUrl ||
                        !/^https?:\/\//i.test(cmUrl) ||
                        !dateCm
                      }
                      className="!rounded-xl gap-2"
                    />
                  </div>
                )}
              </div>
            )}
          </Section>

          {/* 2) LICENCE & ASSURANCE */}
          <Section
            title="2) Licence & Assurance"
            rightAddon={
              <BigCheck
                name="validate_licence"
                label="Validée"
                checked={validateLic}
                onChange={(e) => setValidateLic(e.target.checked)}
              />
            }
          >
            {/* a) Formulaire à signer */}
            <div className="mb-4">
              <div className="mb-2">
                <CurrentDoc
                  label="Formulaire à signer"
                  url={formSignerUrl}
                  onOpen={() => openUrl(formSignerUrl)}
                  onDelete={() =>
                    removeField(
                      "formulaire_licence_a_signer",
                      "Formulaire à signer"
                    )
                  }
                  onReplaceClick={() => {}}
                  replacing={false}
                />
              </div>
              <div className="grid sm:grid-cols-[1fr,auto] gap-3 items-end">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Lien du formulaire à signer
                  </label>
                  <input
                    type="url"
                    value={formSignerUrl}
                    onChange={(e) => setFormSignerUrl(e.target.value)}
                    placeholder="https://drive.google.com/…"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <Button
                  type="button"
                  onClick={async () => {
                    try {
                      setSaving(true);
                      await updateDossierInscription(dossier.id, {
                        formulaire_licence_a_signer:
                          (formSignerUrl || "").trim() || null,
                      });
                      toast.success("Lien mis à jour.");
                      await refreshList?.();
                    } catch (e) {
                      console.error(e);
                      toast.error("Échec de la mise à jour.");
                    } finally {
                      setSaving(false);
                    }
                  }}
                  icon={FaSave}
                  text={saving ? "Enregistrement…" : "Enregistrer"}
                  disabled={saving}
                  className="!rounded-xl gap-2"
                />
              </div>
            </div>

            {/* b) Formulaire signé */}
            <div className="mb-4">
              <CurrentDoc
                label="Formulaire signé"
                url={formSigneeUrl}
                onOpen={() => openUrl(formSigneeUrl)}
                onDelete={() => removeField(SIGNEE_FIELD, "Formulaire signé")}
                onReplaceClick={() => setSigneeReplacing(true)}
                replacing={signeeReplacing}
              />

              {(!hadSigneeInitial || signeeReplacing) && (
                <div className="mt-4 space-y-3">
                  <ModeToggle value={signeeMode} onChange={setSigneeMode} />
                  {signeeMode === "upload" ? (
                    <div className="grid sm:grid-cols-[1fr,auto] gap-3 items-end">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Fichier (PDF/JPG/PNG/WEBP)
                        </label>
                        <input
                          type="file"
                          accept=".pdf,image/*"
                          onChange={(e) =>
                            setSigneeFile(e.target.files?.[0] || null)
                          }
                          className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                        />
                        {isLocalFileUrl(formSigneeUrl) && (
                          <p className="mt-2 text-xs text-slate-500">
                            Un fichier local est déjà présent. Le nouveau le
                            remplacera.
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        onClick={submitSignee}
                        icon={FaUpload}
                        text={
                          uploading.signee ? "Téléversement…" : "Enregistrer"
                        }
                        disabled={
                          uploading.signee ||
                          (signeeMode === "upload" && !signeeFile)
                        }
                        className="!rounded-xl gap-2"
                      />
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-[1fr,auto] gap-3 items-end">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Lien du formulaire signé
                        </label>
                        <input
                          type="url"
                          value={formSigneeUrl}
                          onChange={(e) => setFormSigneeUrl(e.target.value)}
                          placeholder="https://…"
                          className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={submitSignee}
                        icon={FaSave}
                        text={
                          uploading.signee ? "Enregistrement…" : "Enregistrer"
                        }
                        disabled={
                          uploading.signee ||
                          !formSigneeUrl ||
                          !/^https?:\/\//i.test(formSigneeUrl)
                        }
                        className="!rounded-xl gap-2"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* c) Licence définitive */}
            <div>
              <CurrentDoc
                label="Licence / Assurance"
                url={licenceUrl}
                onOpen={() => openUrl(licenceUrl)}
                onDelete={() => removeField("licence_url", "Licence")}
                onReplaceClick={() => setLicReplacing(true)}
                replacing={licReplacing}
              />

              {(!hadLicInitial || licReplacing) && (
                <div className="mt-4 space-y-3">
                  <ModeToggle value={licMode} onChange={setLicMode} />
                  {licMode === "upload" ? (
                    <div className="grid sm:grid-cols-[1fr,auto] gap-3 items-end">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Fichier (PDF/JPG/PNG/WEBP)
                        </label>
                        <input
                          type="file"
                          accept=".pdf,image/*"
                          onChange={(e) =>
                            setLicFile(e.target.files?.[0] || null)
                          }
                          className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                        />
                        {isLocalFileUrl(licenceUrl) && (
                          <p className="mt-2 text-xs text-slate-500">
                            Un fichier local est déjà présent. Le nouveau le
                            remplacera.
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        onClick={submitLicence}
                        icon={FaUpload}
                        text={uploading.lic ? "Téléversement…" : "Enregistrer"}
                        disabled={
                          uploading.lic || (licMode === "upload" && !licFile)
                        }
                        className="!rounded-xl gap-2"
                      />
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-[1fr,auto] gap-3 items-end">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Lien licence / assurance
                        </label>
                        <input
                          type="url"
                          value={licenceUrl}
                          onChange={(e) => setLicenceUrl(e.target.value)}
                          placeholder="https://…"
                          className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={submitLicence}
                        icon={FaSave}
                        text={uploading.lic ? "Enregistrement…" : "Enregistrer"}
                        disabled={
                          uploading.lic ||
                          !licenceUrl ||
                          !/^https?:\/\//i.test(licenceUrl)
                        }
                        className="!rounded-xl gap-2"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </Section>

          {/* 3) GOOGLE DRIVE + 4) VALIDATIONS */}
          <Section
            title="3) Dossier Google Drive & Validations"
            rightAddon={
              <BigCheck
                name="validate_reglement"
                label="Règlement validé"
                checked={validateRegl}
                onChange={(e) => setValidateRegl(e.target.checked)}
              />
            }
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <div className="mb-2">
                  <CurrentDoc
                    label="Dossier Google Drive"
                    url={driveUrl}
                    onOpen={() => openUrl(driveUrl)}
                    onDelete={() => removeField("lien_drive", "Lien Drive")}
                    onReplaceClick={() => {}}
                    replacing={false}
                  />
                </div>
                <label className="block text-sm font-medium mb-1">
                  Lien Drive (facultatif)
                </label>
                <input
                  type="url"
                  value={driveUrl}
                  onChange={(e) => setDriveUrl(e.target.value)}
                  placeholder="https://drive.google.com/…"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                />
              </div>
            </div>
          </Section>
        </div>

        {/* Footer sticky */}
        <div className="sticky bottom-0 z-10">
          <div className="pointer-events-none h-6 bg-gradient-to-t from-white/95 dark:from-slate-900/95 to-transparent" />
          <div className="pointer-events-auto bg-white/95 dark:bg-slate-900/95 border-t border-slate-200/70 dark:border-slate-800/70 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 px-4 py-2 inline-flex items-center gap-2"
              title="Annuler"
            >
              <FaTimes />
              Annuler
            </button>
            <Button
              text={
                saving
                  ? "Enregistrement…"
                  : "Enregistrer les validations & liens"
              }
              icon={FaSave}
              type="button"
              onClick={submitMeta}
              disabled={saving}
              className="!rounded-xl gap-2"
            />
          </div>
        </div>
      </div>

      {/* style: hover delete bouton = fond blanc / texte rouge (déjà sur le bouton) */}
    </div>,
    document.body
  );
}
